import React, { useState } from 'react';
import { Bug, Database, Trash2, RefreshCw, Download, AlertTriangle } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

const DebugTools = () => {
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState<string[]>([]);

  const addLog = (message: string) => {
    setLogs(prev => [...prev, `[${new Date().toLocaleTimeString()}] ${message}`]);
  };

  const testConnection = async () => {
    setLoading(true);
    addLog('Testing database connection...');
    try {
      const { data, error } = await supabase.from('patients').select('count');
      if (error) throw error;
      addLog('✓ Database connection successful');
    } catch (error: any) {
      addLog(`✗ Database connection failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const checkTablesStatus = async () => {
    setLoading(true);
    addLog('Checking database tables...');

    const tables = [
      'patients',
      'appointments',
      'treatments',
      'quotes',
      'quote_items',
      'invoices',
      'invoice_items',
      'patient_files',
      'service_cards',
      'calendar_settings'
    ];

    for (const table of tables) {
      try {
        const { count, error } = await supabase.from(table).select('*', { count: 'exact', head: true });
        if (error) throw error;
        addLog(`✓ Table "${table}": ${count} records`);
      } catch (error: any) {
        addLog(`✗ Table "${table}": ${error.message}`);
      }
    }

    setLoading(false);
  };

  const exportData = async () => {
    setLoading(true);
    addLog('Exporting database data...');

    try {
      const tables = ['patients', 'appointments', 'treatments', 'quotes', 'invoices', 'service_cards'];
      const exportData: Record<string, any> = {};

      for (const table of tables) {
        const { data, error } = await supabase.from(table).select('*');
        if (error) throw error;
        exportData[table] = data;
      }

      const dataStr = JSON.stringify(exportData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `clinic-backup-${new Date().toISOString().split('T')[0]}.json`;
      link.click();

      addLog('✓ Data exported successfully');
    } catch (error: any) {
      addLog(`✗ Export failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const clearLogs = () => {
    setLogs([]);
  };

  return (
    <div>
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Bug className="text-blue-600" />
            Debug Tools
          </h2>
          <p className="text-gray-600 mt-1">Database testing and diagnostic utilities</p>
        </div>
      </div>

      <div className="grid gap-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Database className="text-blue-600" size={20} />
            Database Tools
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <button
              onClick={testConnection}
              disabled={loading}
              className="bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
              Test Connection
            </button>

            <button
              onClick={checkTablesStatus}
              disabled={loading}
              className="bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <Database size={18} />
              Check Tables
            </button>

            <button
              onClick={exportData}
              disabled={loading}
              className="bg-purple-600 text-white px-4 py-3 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <Download size={18} />
              Export Data
            </button>

            <button
              onClick={clearLogs}
              className="bg-gray-600 text-white px-4 py-3 rounded-lg hover:bg-gray-700 transition-colors flex items-center justify-center gap-2"
            >
              <Trash2 size={18} />
              Clear Logs
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">System Logs</h3>

          {logs.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <AlertTriangle size={48} className="mx-auto mb-3 opacity-50" />
              <p>No logs yet. Run a diagnostic tool to see results.</p>
            </div>
          ) : (
            <div className="bg-gray-900 rounded-lg p-4 font-mono text-sm text-green-400 max-h-96 overflow-y-auto">
              {logs.map((log, index) => (
                <div key={index} className="mb-1">
                  {log}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-yellow-600 flex-shrink-0" size={24} />
            <div>
              <h3 className="text-lg font-semibold text-yellow-900 mb-2">Warning</h3>
              <p className="text-yellow-800 text-sm">
                These tools are for debugging and diagnostic purposes only. Use with caution in production environments.
                Always backup your data before performing any operations that modify the database.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">System Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="border border-gray-200 rounded p-3">
              <div className="text-gray-600">Environment</div>
              <div className="font-semibold text-gray-900">{import.meta.env.MODE}</div>
            </div>
            <div className="border border-gray-200 rounded p-3">
              <div className="text-gray-600">Supabase URL</div>
              <div className="font-semibold text-gray-900 truncate">{import.meta.env.VITE_SUPABASE_URL}</div>
            </div>
            <div className="border border-gray-200 rounded p-3">
              <div className="text-gray-600">Browser</div>
              <div className="font-semibold text-gray-900">{navigator.userAgent.split(' ').slice(-2).join(' ')}</div>
            </div>
            <div className="border border-gray-200 rounded p-3">
              <div className="text-gray-600">Timestamp</div>
              <div className="font-semibold text-gray-900">{new Date().toLocaleString()}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DebugTools;
